import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Volume2, VolumeX, PartyPopper, Activity, Square, Play as PlayIcon, Radio as Radio2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CityBackdrop } from "@/components/menu/CityBackdrop";
import { useMenuAudio } from "@/components/menu/useMenuAudio";
import { useStationPlayer, STATIONS } from "@/components/cef/phoneMusic";
import { BootSequence } from "@/components/menu/BootSequence";
import { TitleScreen } from "@/components/menu/TitleScreen";
import { MainMenuNav } from "@/components/menu/MainMenuNav";
import { ServerBrowser } from "@/components/menu/ServerBrowser";
import { SettingsPanel, CreditsPanel, CharacterPanel } from "@/components/menu/MenuPanels";
import { SocialPanel, RoadmapPanel, LiveTicker, TourOverlay, NewsPanel } from "@/components/menu/MenuExtras";
import { WorkshopPanel } from "@/components/menu/WorkshopPanel";
import { SocialHubPanel } from "@/components/menu/SocialHubPanel";
import { MenuWikiPanel } from "@/components/menu/MenuWikiPanel";
import { ModCheckModal } from "@/components/menu/ModCheckModal";
import { useTesterTelemetry } from "@/components/menu/TesterLinkPanel";
import { activeTheme, allThemes, BUILTIN_THEMES, setActiveTheme } from "@/components/menu/menuThemes";
import { activePack } from "@/components/cef/packs";
import { useMenuWorld } from "@/components/menu/useMenuWorld";
import { WantedDock, IntelRail, DeadDrop, BreakingNews } from "@/components/menu/LivingMenuLayers";
import { TosGate, InboxBell, useTosGate } from "@/components/menu/MenuSocial";
import { useI18n } from "@/i18n";

/* NEW CITIZEN — AAA startup & main menu (UE5_STARTUP_MENU_SPEC.md, web/CEF build).
   Boot state machine: boot → title → menu ⇄ browser/settings/credits/character → travelling → quit.
   Borderless 1920x1080-ready; in-game it renders inside an Unreal Web Browser widget.
   Menu themes come from workshop config packs (see components/menu/menuThemes.js). */

const ZOOM = { boot: 1.14, title: 1.0, menu: 1.06, browser: 1.1, workshop: 1.09, wiki: 1.08, social: 1.1, settings: 1.08, credits: 1.08, character: 1.07, travelling: 1.28, quitting: 1.1, quit: 1.1 };

const GRAIN_URL = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>")`;

const CSS = `
  @keyframes ncCrt { 0% { width: 0; opacity: 0; } 30% { opacity: 1; } 100% { width: 100%; opacity: 1; } }
  @keyframes ncFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes ncFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
  @keyframes ncBlink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
  @keyframes ncPulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
  @keyframes ncBreathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.015); } }
  @keyframes ncReveal { 0% { opacity: 0; transform: translateY(42px) skewX(10deg); filter: blur(8px); } 60% { opacity: 1; filter: blur(0); text-shadow: 2px 0 #f43f5e, -2px 0 #22d3ee; } 100% { opacity: 1; transform: none; filter: blur(0); text-shadow: none; } }
  @keyframes ncGlitch { 0% { opacity: 0; transform: translateX(-6px); text-shadow: 3px 0 #f43f5e, -3px 0 #22d3ee; } 20% { opacity: 1; transform: translateX(5px); } 40% { transform: translateX(-3px); text-shadow: -3px 0 #f43f5e, 3px 0 #22d3ee; } 60% { transform: translateX(2px); } 100% { transform: none; text-shadow: none; } }
  @keyframes ncSlideL { from { opacity: 0; transform: translateX(-28px); } to { opacity: 1; transform: none; } }
  @keyframes ncSlideR { from { opacity: 0; transform: translateX(48px); } to { opacity: 1; transform: none; } }
  @keyframes ncLogoCard { 0% { opacity: 0; } 12% { opacity: 1; } 86% { opacity: 1; } 100% { opacity: 0; } }
  @keyframes ncCredits { from { transform: translateY(100%); } to { transform: translateY(-100%); } }
  @keyframes ncGrain { 0% { transform: translate(0,0); } 25% { transform: translate(-1.6%,1.2%); } 50% { transform: translate(1.2%,-1%); } 75% { transform: translate(-1%,-1.6%); } 100% { transform: translate(0,0); } }
  @keyframes ncWipeIn { from { transform: translateX(-130%) skewX(-14deg); } to { transform: translateX(0) skewX(-14deg); } }
  @keyframes ncFlash { 0% { opacity: 0; } 18% { opacity: 0.85; } 100% { opacity: 0; } }
  @keyframes ncDots { 0% { content: ""; } 33% { content: "."; } 66% { content: ".."; } 100% { content: "..."; } }
  .nc-grain { position: absolute; inset: -60%; width: 220%; height: 220%; background-image: ${GRAIN_URL}; opacity: 0.05; animation: ncGrain 0.9s steps(4) infinite; pointer-events: none; }
  .nc-scan { background-image: repeating-linear-gradient(0deg, rgba(255,255,255,0.35) 0px, rgba(255,255,255,0.35) 1px, transparent 1px, transparent 4px); }
  .nc-travelling-dots::after { content: ""; animation: ncDots 1.2s steps(1) infinite; }
`;

function useServers() {
  const [list, setList] = useState([]);
  const [steamList, setSteamList] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const cache = useRef({ at: 0, data: [], steam: [] });

  const refresh = useCallback((force = false) => {
    const now = Date.now();
    if (!force && cache.current.at && now - cache.current.at < 60000) {
      setList(cache.current.data); setSteamList(cache.current.steam); setFetchedAt(cache.current.at);
      return Promise.resolve(cache.current.data);
    }
    setLoading(true); setError(""); setCooldown(5);
    return Promise.all([
      api.get("/nc/servers"),
      api.get("/nc/servers/steam").catch(() => ({ data: { servers: [] } })),
    ]).then(([r, st]) => {
      const arr = r.data.servers || [];
      const steam = st.data.servers || [];
      cache.current = { at: Date.now(), data: arr, steam };
      setList(arr); setSteamList(steam); setFetchedAt(Date.now());
      return arr;
    }).catch((e) => { setError(e.response?.data?.detail || "Hub unreachable — retry in a moment"); return cache.current.data; })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  return { list, steamList, fetchedAt, loading, error, cooldown, refresh };
}

export default function MenuCefPage() {
  const navigate = useNavigate();
  const audio = useMenuAudio();
  const radio = useStationPlayer();
  const { t } = useI18n();
  const servers = useServers();
  // main-menu theme — stock look or a workshop config pack (Settings → Menu Theme)
  const [themeId, setThemeId] = useState(() => activeTheme().id);
  const theme = useMemo(() => allThemes().find((t) => t.id === themeId) || BUILTIN_THEMES[0], [themeId]);
  // living menu — one aggregate drives backdrop, tickers, posters, drops and bleed
  const { world, refresh: refreshWorld } = useMenuWorld(true);
  const [ghosts, setGhosts] = useState(0); // server-browser hover → silhouettes in the city
  // reputation bleed — a returning cop's menu glows blue, a criminal's bleeds red
  const FACTION_ACCENTS = { law: "#3b82f6", criminal: "#ef4444" };
  const ACCENT = FACTION_ACCENTS[world?.me?.faction] || theme.accent;
  const { needsTos, tosDone } = useTosGate(true);
  const pickTheme = (id) => { setActiveTheme(id); setThemeId(id); };
  // workshop packs: boot sequence variant + theme typography depth
  const [packsRev, setPacksRev] = useState(0);
  const bootPack = useMemo(() => activePack("boot_pack")?.pack?.boot || null, [packsRev]); // eslint-disable-line react-hooks/exhaustive-deps
  const MENU_FONTS = { default: "", mono: "'JetBrains Mono','Courier New',monospace", serif: "Georgia,'Times New Roman',serif", stencil: "'Copperplate Gothic Bold','Arial Black',sans-serif" };
  const menuFont = MENU_FONTS[theme.font] || "";
  const [state, setState] = useState(() => {
    // ?resume=menu|browser drops a returning player straight back into the menu
    // (used by the in-game escape bar so nobody re-watches the boot on the way back)
    const r = new URLSearchParams(window.location.search).get("resume");
    return ["menu", "browser"].includes(r) ? r : "boot";
  });
  const [flash, setFlash] = useState(false);
  const [settled, setSettled] = useState(false);
  const [travellingTo, setTravellingTo] = useState(null);
  const [menuNote, setMenuNote] = useState("");
  const [clock, setClock] = useState("");
  const [railTab, setRailTab] = useState("wire");
  const [tour, setTour] = useState(false);
  const [muted, setMuted] = useState(false);
  const [directive, setDirective] = useState(null);
  const seenDir = useRef(null);

  // unlock the Web Audio context on the VERY FIRST gesture (even during the intro),
  // so the boot sequence's teletype/CRT SFX are audible without needing a skip press
  useEffect(() => {
    const unlock = () => audio.ensure();
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => { window.removeEventListener("keydown", unlock); window.removeEventListener("pointerdown", unlock); };
  }, [audio]);

  // party move: leader issues a directive, members get a follow banner (10s poll)
  useEffect(() => {
    const poll = () => {
      const party = localStorage.getItem("nc_party") || "";
      if (!party) { setDirective(null); return; }
      api.get("/nc/presence/online", { params: { party } }).then((r) => {
        const d = r.data.directive;
        if (d && d.at !== seenDir.current) setDirective(d);
      }).catch(() => {});
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, []);
  const zoomRef = useRef(1.14);
  const zoomTarget = useRef(1.14);

  // smooth camera-drift zoom between states
  useEffect(() => {
    let raf;
    const tickFn = () => {
      zoomTarget.current = ZOOM[state] ?? 1.05;
      zoomRef.current += (zoomTarget.current - zoomRef.current) * 0.045;
      raf = requestAnimationFrame(tickFn);
    };
    raf = requestAnimationFrame(tickFn);
    return () => cancelAnimationFrame(raf);
  }, [state]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date().toLocaleTimeString([], { hour12: false })), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { servers.refresh(); }, [servers.refresh]);

  // atmospheric crime weather — siren/heli layers follow the server's live crime mood
  useEffect(() => { audio.setCrime?.(muted ? 0 : (world?.city?.crime ?? 0)); }, [world?.city?.crime, muted, audio]);

  const stats = (servers.list.length || servers.steamList.length)
    ? {
        online: servers.list.filter((s) => s.online).length + servers.steamList.length,
        players: servers.list.reduce((n, s) => n + (s.snapshot?.players ?? 0), 0) + servers.steamList.reduce((n, s) => n + (s.players ?? 0), 0),
      }
    : null;

  const enterMenu = useCallback(() => {
    audio.ensure(); audio.start(); audio.startAmbience(); audio.startMusic();
    setFlash(true);
    setTimeout(() => setFlash(false), 700);
    setState("menu");
    if (!localStorage.getItem("nc_menu_tour_v2")) setTour(true);
  }, [audio]);

  // keep the wire hot — server list auto-refresh while in menu/browser
  useEffect(() => {
    if (state !== "menu" && state !== "browser") return;
    const t = setInterval(() => servers.refresh(true), 45000);
    return () => clearInterval(t);
  }, [state, servers.refresh]);

  // any-key advance for boot / title — also the audio-unlock gesture
  useEffect(() => {
    if (state !== "boot" && state !== "title") return;
    const any = () => {
      audio.ensure(); audio.startAmbience(); audio.startMusic();
      if (state === "boot") { audio.whoosh(0.1); setState("title"); audio.boom(); }
      else enterMenu();
    };
    window.addEventListener("keydown", any);
    window.addEventListener("pointerdown", any);
    return () => { window.removeEventListener("keydown", any); window.removeEventListener("pointerdown", any); };
  }, [state, enterMenu, audio]);

  useEffect(() => {
    if (state !== "title") return;
    const t = setTimeout(() => setSettled(true), 1600);
    return () => clearTimeout(t);
  }, [state]);

  // escape = back
  useEffect(() => {
    if (!["browser", "workshop", "settings", "credits", "character", "wiki"].includes(state)) return;
    const onKey = (e) => { if (e.key === "Escape") { audio.back(); setState("menu"); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, audio]);

  const [modCheck, setModCheck] = useState(null); // {server, action: "join"|"steam"}
  const [modDl, setModDl] = useState({}); // mod_id -> true once downloaded this session

  const join = useCallback((server, force = false) => {
    if (!force && (server.active_mods || []).length > 0) { setModCheck({ server, action: "join" }); return; }
    audio.connect(); audio.setMuffle(true);
    setTravellingTo(server);
    setState("travelling");
    setTimeout(() => navigate(`/cef/loading/${server.id}`), 2300);
  }, [audio, navigate]);

  const joinSteam = useCallback((server, force = false) => {
    if (!force && (server.active_mods || []).length > 0) { setModCheck({ server, action: "steam" }); return; }
    audio.connect(); audio.setMuffle(true);
    setTravellingTo(server);
    setState("travelling");
    setTimeout(() => {
      try {
        if (window.ue?.gamebridge?.joinserver) window.ue.gamebridge.joinserver(server.address);
        else if (window.ue?.gamebridge?.joinServer) window.ue.gamebridge.joinServer(server.address);
        else window.location.href = `steam://connect/${server.address}`;
      } catch { /* preview fallback below */ }
      // return to the browser with a note (in-game the client travels and this surface unloads)
      setTimeout(() => {
        audio.setMuffle(false);
        setTravellingTo(null);
        setState("browser");
        setMenuNote(t("menu.steam_launch", { addr: server.address }));
        setTimeout(() => setMenuNote(""), 8000);
      }, 1600);
    }, 1400);
  }, [audio]);

  const quickMatch = useCallback(async () => {
    const arr = await servers.refresh(true);
    const hubLive = (arr || []).filter((s) => s.online);
    if (hubLive.length) {
      join(hubLive.sort((a, b) => (b.snapshot?.players ?? 0) - (a.snapshot?.players ?? 0))[0]);
      return;
    }
    const steamLive = servers.steamList;
    if (steamLive.length) {
      joinSteam(steamLive.slice().sort((a, b) => (b.players ?? 0) - (a.players ?? 0))[0]);
      return;
    }
    setMenuNote(t("menu.no_servers"));
    setTimeout(() => setMenuNote(""), 4000);
  }, [servers, join, joinSteam]);

  const [quitConfirm, setQuitConfirm] = useState(false);
  useTesterTelemetry();

  const continueLast = useCallback(async () => {
    const c = world?.me?.continue;
    if (!c) return;
    const arr = await servers.refresh(true);
    const s = (arr || []).find((x) => x.id === c.server_id);
    if (s && s.online) { join(s); return; }
    audio.back();
    setMenuNote(`${c.server_name} is offline right now — pick another world`);
    setTimeout(() => setMenuNote(""), 5000);
  }, [world, servers, join, audio]);

  const quit = useCallback(() => {
    setQuitConfirm(false);
    audio.back();
    setState("quitting");
    setTimeout(() => {
      setState("quit");
      try { window.ue?.gamebridge?.quit?.(); } catch { /* browser preview */ }
      try { window.close(); } catch { /* browsers only allow script-closing script-opened windows — the ended screen is the fallback */ }
    }, 550);
  }, [audio]);

  const onSelect = useCallback((id) => {
    if (id === "quick") { quickMatch(); return; }
    if (id === "quit") { audio.click(); setQuitConfirm(true); return; }
    audio.click();
    setState(id);
  }, [audio, quickMatch, quit]);

  const backToMenu = useCallback(() => { audio.back(); setState("menu"); }, [audio]);
  const onBootDone = useCallback(() => { setState("title"); audio.boom(); }, [audio]);

  const partyInfo = {
    code: localStorage.getItem("nc_party") || "",
    isLeader: localStorage.getItem("nc_party_host") === "1",
  };

  const onPartyMove = useCallback((server) => {
    const party = localStorage.getItem("nc_party");
    if (!party) return;
    api.post("/nc/presence/party/directive", {
      anon_id: localStorage.getItem("nc_anon") || "",
      party,
      server: { source: server.source, id: server.source === "hub" ? server.id : "", address: server.address || "", name: server.name },
    }).then((r) => {
      seenDir.current = r.data.directive.at;
      toast.success("Party move called — your party sees a follow banner");
    }).catch((e) => toast.error(e.response?.data?.detail || "Party move failed"));
    (server.source === "steam" ? joinSteam : join)(server);
  }, [join, joinSteam]);

  const inMenu = state === "menu";
  const inPanels = ["browser", "workshop", "settings", "credits", "character", "wiki", "social"].includes(state);
  const topServer = stats && servers.list.filter((s) => s.online).sort((a, b) => (b.snapshot?.players ?? 0) - (a.snapshot?.players ?? 0))[0];

  return (
    <div data-testid="cef-menu" id="nc-menu" className="fixed inset-0 overflow-hidden bg-black text-zinc-100 select-none"
      style={{ fontFamily: menuFont || "'Rajdhani', 'Segoe UI', sans-serif" }}>
      {theme.buttons === "pill" && <style>{`#nc-menu nav button, #nc-menu nav a { border-radius: 999px !important; }`}</style>}
      {world?.backdrop?.url && (
        <img data-testid="menu-backdrop-photo" src={world.backdrop.url} alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40 saturate-[0.7]" aria-hidden="true" />
      )}
      <CityBackdrop accent={ACCENT} zoomRef={zoomRef} world={world} ghosts={state === "browser" ? ghosts : 0} />
      {needsTos && <TosGate accent={ACCENT} onDone={tosDone} />}
      {modCheck && (
        <ModCheckModal server={modCheck.server} accent={ACCENT}
          onClose={() => setModCheck(null)}
          onProceed={() => { const { server, action } = modCheck; setModCheck(null); action === "join" ? join(server, true) : joinSteam(server, true); }} />
      )}
      {/* workshop menu-theme pack — tricolor stripe + tint */}
      {theme.stripes.length > 0 && (
        <div data-testid="theme-stripes" className="absolute top-0 inset-x-0 z-40 flex h-1.5">
          {theme.stripes.map((c, i) => <span key={i} className="flex-1" style={{ backgroundColor: c }} />)}
        </div>
      )}
      {theme.tint && <div className="absolute inset-0 pointer-events-none z-[1]" style={{ backgroundColor: theme.tint, opacity: 0.45, mixBlendMode: "multiply" }} />}

      {/* film grain + scanlines + vignette */}
      <div className="nc-grain" />
      <div className="absolute inset-0 nc-scan opacity-[0.035] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 52%, rgba(0,0,0,0.55) 100%)" }} />

      {state === "boot" && <BootSequence onDone={onBootDone} audio={audio} theme={theme} boot={bootPack} />}
      {state === "title" && <TitleScreen stats={stats} settled={settled} />}

      {/* white flash on title → menu */}
      {flash && <div className="absolute inset-0 bg-white pointer-events-none z-40" style={{ animation: "ncFlash 0.65s ease both" }} />}

      {(inMenu || inPanels) && (
        <>
          {/* top chrome */}
          <div className="absolute top-0 inset-x-0 flex items-center justify-between px-6 sm:px-10 py-5 z-20 pointer-events-none" style={{ animation: "ncFadeIn 0.4s ease both" }}>
            <div className="flex items-center gap-3 pointer-events-auto">
              <span className="w-2.5 h-2.5" style={{ backgroundColor: ACCENT, boxShadow: `0 0 14px ${ACCENT}` }} />
              <span className="font-bold uppercase tracking-[0.3em] text-sm text-zinc-100">New Citizen</span>
              <span className="font-mono text-[8px] tracking-[0.3em] text-zinc-600 uppercase mt-0.5">// {state === "menu" ? t("menu.main") : t(`nav.${state}`)}</span>
            </div>
            <div className="flex items-center gap-5 font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500 pointer-events-auto">
              <div className="relative"><InboxBell accent={ACCENT} active={inMenu || state === "browser"} /></div>
              {/* Boombox in the menu — the city's synthesized stations, live in the title screen */}
              <div data-testid="menu-radio" className="flex items-center border border-white/15">
                <button data-testid="menu-radio-toggle" onClick={() => {
                  if (radio.playingId) { radio.stop(); }
                  else { radio.play(STATIONS[0].id); }
                }} className="flex items-center gap-1.5 hover:border-white/40 px-2.5 py-1.5 transition-colors text-zinc-300">
                  {radio.playingId ? <Square className="w-3 h-3 fill-current" /> : <PlayIcon className="w-3 h-3" />}
                  {radio.playingId ? "Stop" : "Radio"}
                </button>
                {radio.playingId && (
                  <button data-testid="menu-radio-next" onClick={() => {
                    const i = STATIONS.findIndex((s) => s.id === radio.playingId);
                    radio.play(STATIONS[(i + 1) % STATIONS.length].id);
                  }} className="flex items-center gap-1.5 border-l border-white/15 px-2.5 py-1.5 text-zinc-400 hover:text-zinc-100 transition-colors">
                    <Radio2 className="w-3 h-3 animate-pulse" style={{ color: (STATIONS.find((s) => s.id === radio.playingId) || {}).color }} />
                    {(STATIONS.find((s) => s.id === radio.playingId) || {}).name}
                  </button>
                )}
              </div>
              <button data-testid="music-toggle" onClick={() => { const m = !muted; setMuted(m); audio.setVolumes({ master: m ? 0 : 0.8 }); if (!m) audio.tick(); }}
                className="flex items-center gap-1.5 border border-white/15 hover:border-white/40 px-2.5 py-1.5 transition-colors text-zinc-300">
                {muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />} {muted ? t("menu.muted") : t("menu.sound")}
              </button>
              <span data-testid="menu-hub-status">{stats ? `${stats.online} ${t("browser.live")} · ${stats.players} ${t("browser.citizens")}` : t("menu.linking")}</span>
              <span data-testid="menu-clock" className="text-zinc-400">{clock}</span>
            </div>
          </div>

          {inMenu && (
            <div className="absolute inset-0 flex items-center z-10">
              <div className="pl-[8%] sm:pl-[18%]">
                <MainMenuNav onSelect={onSelect} audio={audio} accent={ACCENT} />
                {menuNote && <p data-testid="menu-note" className="mt-4 font-mono text-[9px] tracking-[0.25em] uppercase text-amber-400" style={{ animation: "ncFadeIn 0.3s ease both" }}>{menuNote}</p>}
              </div>

              {/* right command deck — docked, fully contained, bigger type */}
              <div className="hidden lg:flex flex-col absolute right-8 top-[96px] bottom-[76px] w-[400px] border border-white/12 bg-[#070a10]/90 backdrop-blur-md"
                style={{ animation: "ncSlideR 0.5s cubic-bezier(0.16,1,0.3,1) 0.35s both", boxShadow: "0 0 50px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.03)" }}>
                {/* corner brackets */}
                <span className="absolute -top-px -left-px w-4 h-4 border-t-2 border-l-2" style={{ borderColor: ACCENT }} />
                <span className="absolute -bottom-px -right-px w-4 h-4 border-b-2 border-r-2" style={{ borderColor: ACCENT }} />
                <div className="flex border-b border-white/10 shrink-0">
                  {[["wire", t("rail.wire")], ["news", t("rail.news")], ["citizens", t("rail.citizens")], ["roadmap", t("rail.roadmap")]].map(([id, label]) => (
                    <button key={id} data-testid={`rail-tab-${id}`} onClick={() => { setRailTab(id); audio.tick(); }}
                      className={`flex-1 py-3.5 font-mono text-[11px] font-bold tracking-[0.22em] uppercase transition-colors border-b-2 ${railTab === id ? "text-zinc-50" : "text-zinc-400 hover:text-zinc-100 border-transparent"}`}
                      style={railTab === id ? { borderBottomColor: ACCENT, backgroundColor: `${ACCENT}12` } : {}}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex-1 min-h-0 p-5 overflow-y-auto" style={{ scrollbarWidth: "thin", scrollbarColor: `${ACCENT}55 transparent` }}>
                  {railTab === "wire" && (
                    <div className="flex flex-col gap-4">
                      {world?.city?.market_event && (
                        <div data-testid="market-shock-banner" className={`border p-4 ${world.city.market_event.drift >= 0 ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-red-500/40 bg-red-500/[0.06]"}`}>
                          <p className={`font-mono text-[9px] font-bold uppercase tracking-[0.3em] flex items-center gap-2 ${world.city.market_event.drift >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            <Activity className="w-3.5 h-3.5 animate-pulse" /> Market shock — {world.city.market_event.label}
                          </p>
                          <p className="font-mono text-[9px] text-zinc-300 mt-1.5 leading-relaxed">{world.city.market_event.blurb}</p>
                          <p className="font-mono text-[7px] text-zinc-600 mt-1.5 uppercase tracking-[0.2em]">runs until {new Date(world.city.market_event.ends_at).toLocaleString()}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2.5">
                        <div className="border border-white/10 bg-black/40 p-4">
                          <p data-testid="wire-live-count" className="font-bold text-3xl leading-none text-zinc-50">{stats ? stats.online : "—"}</p>
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-2 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t("wire.servers_live")}</p>
                        </div>
                        <div className="border border-white/10 bg-black/40 p-4">
                          <p data-testid="wire-citizen-count" className="font-bold text-3xl leading-none text-zinc-50">{stats ? stats.players : "—"}</p>
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-2">{t("wire.citizens")}</p>
                        </div>
                      </div>
                      <div className="border border-white/10 bg-black/40 p-4">
                        <p className="font-mono text-[10px] tracking-[0.3em] uppercase mb-2.5" style={{ color: ACCENT }}>{t("wire.busiest")}</p>
                        {topServer ? (
                          <div data-testid="menu-wire">
                            <p className="font-bold uppercase tracking-wide text-lg text-zinc-50 leading-tight">{topServer.name}</p>
                            <p className="font-mono text-[11px] text-zinc-300 mt-1.5">{topServer.snapshot?.players ?? 0} {t("browser.citizens")} · {topServer.mode}</p>
                          </div>
                        ) : (
                          <p className="font-mono text-[11px] text-zinc-300 leading-relaxed">
                            {servers.steamList.length > 0
                              ? t("wire.steam_live", { n: servers.steamList.length })
                              : t("wire.none")}
                          </p>
                        )}
                      </div>
                      <div className="border border-white/10 bg-black/40 p-4">
                        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-2">{t("wire.feed")}</p>
                        <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-[0.15em]">{t("wire.autorefresh")}</p>
                      </div>
                    </div>
                  )}
                  {railTab === "news" && <NewsPanel world={world} accent={ACCENT} />}
                  {railTab === "citizens" && <SocialPanel accent={ACCENT} audio={audio} scene={state} />}
                  {railTab === "roadmap" && <RoadmapPanel accent={ACCENT} />}
                </div>
              </div>
            </div>
          )}

          {inMenu && (
            <>
              {/* living-menu layers — the intel rail + wanted dock read the city */}
              <IntelRail world={world} accent={ACCENT} onRefresh={refreshWorld} onContinue={continueLast} />
              <WantedDock wanted={world?.wanted} accent={ACCENT} />
              <DeadDrop world={world} onOpened={refreshWorld} />
            </>
          )}

          {state === "browser" && <ServerBrowser servers={servers} onJoin={join} onJoinSteam={joinSteam} onBack={backToMenu} audio={audio} accent={ACCENT} partyInfo={partyInfo} onPartyMove={onPartyMove} onHoverServer={(s) => setGhosts(s ? Math.min(14, s.snapshot?.players ?? s.players ?? 4) : 0)} />}
          {state === "workshop" && <WorkshopPanel onBack={backToMenu} audio={audio} accent={ACCENT} />}
          {state === "wiki" && <MenuWikiPanel onBack={backToMenu} audio={audio} accent={ACCENT} />}
          {state === "social" && <SocialHubPanel onBack={backToMenu} audio={audio} accent={ACCENT} />}
          {state === "settings" && <SettingsPanel audio={audio} onBack={backToMenu} accent={ACCENT} theme={theme} onTheme={pickTheme} onPacksChange={() => setPacksRev((r) => r + 1)} />}
          {state === "credits" && <CreditsPanel onBack={backToMenu} accent={ACCENT} />}
          {state === "character" && <CharacterPanel onBack={backToMenu} accent={ACCENT} audio={audio} />}

          {inMenu && <div className="absolute bottom-12 inset-x-0 hidden md:flex justify-center z-10 px-40"><LiveTicker stats={stats} accent={ACCENT} /></div>}
          <p className="absolute bottom-5 right-6 sm:right-10 font-mono text-[8px] tracking-[0.3em] text-zinc-500 uppercase z-20">build 4.0.2 · specpixel.com</p>
          <p className="absolute bottom-5 left-6 sm:left-10 font-mono text-[8px] tracking-[0.3em] text-zinc-500 uppercase z-20">{t("hint.keys")}</p>

          {tour && inMenu && <TourOverlay accent={ACCENT} onDone={() => { localStorage.setItem("nc_menu_tour_v2", "1"); setTour(false); }} />}
        </>
      )}

      {/* party move banner */}
      {directive && !["travelling", "quitting", "quit"].includes(state) && (
        <div data-testid="party-directive" className="absolute top-20 left-1/2 -translate-x-1/2 z-40 border bg-[#080a0f]/95 backdrop-blur-md px-6 py-4 flex items-center gap-5"
          style={{ borderColor: `${ACCENT}66`, boxShadow: `0 0 50px ${ACCENT}33`, animation: "ncFadeUp 0.3s ease both" }}>
          <PartyPopper className="w-5 h-5 shrink-0" style={{ color: ACCENT }} />
          <div>
            <p className="font-mono text-[9px] tracking-[0.3em] uppercase" style={{ color: ACCENT }}>{t("party.move")}</p>
            <p className="font-semibold text-sm text-zinc-100 mt-0.5">{directive.by} {t("party.taking")} <span style={{ color: ACCENT }}>{directive.server?.name}</span></p>
          </div>
          <button data-testid="party-follow" onClick={() => {
            const s = directive.server;
            setDirective(null);
            if (s?.source === "steam") joinSteam({ source: "steam", address: s.address, name: s.name });
            else join({ id: s.id, name: s.name });
          }} className="px-5 py-2.5 font-bold uppercase tracking-[0.2em] text-[11px] text-black transition-transform hover:scale-105" style={{ backgroundColor: ACCENT }}>
            {t("party.follow")}
          </button>
          <button data-testid="party-dismiss" onClick={() => { seenDir.current = directive.at; setDirective(null); }}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-100 transition-colors">{t("party.dismiss")}</button>
        </div>
      )}

      {/* travelling wipe (spec §3.3) */}
      {state === "travelling" && (
        <div data-testid="wipe-overlay" className="absolute inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 flex">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex-1 bg-[#05070d] border-r border-[#2E6BFF]/25"
                style={{ animation: `ncWipeIn 0.28s cubic-bezier(0.7,0,0.3,1) ${i * 0.045}s both` }} />
            ))}
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <p className="font-mono text-[10px] tracking-[0.5em] uppercase" style={{ color: ACCENT, animation: "ncFadeIn 0.4s ease 0.5s both" }}>{t("travel.uplink")}</p>
            <p data-testid="travelling-server" className="font-bold uppercase tracking-[0.2em] text-2xl sm:text-3xl text-zinc-50 nc-travelling-dots" style={{ animation: "ncFadeIn 0.4s ease 0.65s both" }}>
              {travellingTo?.name || "server"}
            </p>
            <div className="w-56 h-[3px] bg-white/10 overflow-hidden" style={{ animation: "ncFadeIn 0.4s ease 0.8s both" }}>
              <div className="h-full" style={{ backgroundColor: ACCENT, boxShadow: `0 0 12px ${ACCENT}`, animation: "ncProgress 1.4s cubic-bezier(0.3,0.8,0.4,1) 0.8s both" }} />
            </div>
          </div>
        </div>
      )}

      {/* quit confirmation — are you sure? */}
      {quitConfirm && (
        <div data-testid="quit-confirm" className="absolute inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-6"
          style={{ animation: "ncFadeIn 0.2s ease both" }} onClick={() => setQuitConfirm(false)}>
          <div className="w-full max-w-md border border-white/12 bg-[#070a10]/95 p-8 text-center" style={{ animation: "ncFadeUp 0.25s ease both" }} onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[9px] tracking-[0.4em] uppercase text-zinc-500 mb-2">Leaving the city</p>
            <h3 className="font-bold uppercase tracking-[0.15em] text-2xl text-zinc-50">Quit New Citizen?</h3>
            <p className="font-mono text-[10px] text-zinc-400 mt-3 leading-relaxed">The city keeps breathing without you.<br />Unsaved menu fiddling will be lost.</p>
            <div className="flex gap-3 mt-7">
              <button data-testid="quit-no" onClick={() => { audio.tick(); setQuitConfirm(false); }}
                className="flex-1 py-3.5 font-mono text-[10px] font-bold tracking-[0.3em] uppercase text-black transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: ACCENT }}>
                No — stay
              </button>
              <button data-testid="quit-yes" onClick={quit}
                className="flex-1 py-3.5 font-mono text-[10px] font-bold tracking-[0.3em] uppercase border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors">
                Yes — quit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* quit */}
      {(state === "quitting" || state === "quit") && (
        <div className="absolute inset-0 bg-black z-50 flex items-center justify-center" style={{ animation: "ncFadeIn 0.45s ease both", opacity: state === "quitting" ? undefined : 1 }}>
          {state === "quit" && (
            <div data-testid="quit-screen" className="text-center" style={{ animation: "ncFadeUp 0.6s ease both" }}>
              <p className="font-mono text-[10px] tracking-[0.5em] uppercase text-zinc-500 mb-3">{t("quit.ended")}</p>
              <p className="font-bold uppercase tracking-[0.2em] text-2xl text-zinc-200">{t("quit.city")}</p>
              <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-600 mt-4">{t("quit.close")} <button data-testid="quit-restart" className="underline" style={{ color: ACCENT }} onClick={() => window.location.reload()}>{t("quit.boot")}</button></p>
            </div>
          )}
        </div>
      )}

      <style>{CSS}{`@keyframes ncProgress { from { width: 0%; } to { width: 100%; } }
@keyframes ncWordmarkIn { 0% { opacity: 0; transform: scale(1.12); filter: blur(14px); clip-path: inset(0 50% 0 50%); } 55% { opacity: 1; filter: blur(0); clip-path: inset(0 0 0 0); } 70% { transform: scale(0.995); } 100% { opacity: 1; transform: scale(1); filter: blur(0); clip-path: inset(0 0 0 0); } }`}</style>
    </div>
  );
}
